import TransactionsType from "../../enums/TransactionsType";

import Zoom from "@mui/material/Zoom";
import { Tooltip } from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import type { HugeiconsIconProps, IconSvgElement } from "@hugeicons/react";
import {
  Medal06Icon,
  SmartPhone04Icon,
} from "@hugeicons-pro/core-twotone-rounded";
import { useTranslation } from "react-i18next";
import { Chip } from "@outpave/ds";
import {
  BankIcon,
  Cash01Icon,
  Coins01Icon,
  CreditCardPosIcon,
  JusticeScale01Icon,
  ShoppingCart01Icon,
} from "@hugeicons-pro/core-stroke-standard";
import { Car03Icon } from "@hugeicons-pro/core-stroke-rounded";
import { Payment02Icon } from "@hugeicons-pro/core-stroke-sharp";

export enum RenderOption {
  ICON = "ICON",
  TEXT = "TEXT",
}

function toTitleCase(str: string) {
  return str?.charAt(0).toUpperCase() + str?.slice(1).toLowerCase();
}

type TransactionTypeMeta = {
  icon: IconSvgElement;
  /** Canonical English label, translated at the point of render. */
  name: string;
};

/**
 * The one place a transaction type resolves to its glyph and label. The three
 * renderers below differ only in what they do with the pair.
 */
const transactionTypeMeta = (
  transactionType: TransactionsType,
  cardType?: string,
): TransactionTypeMeta | null => {
  switch (transactionType) {
    case TransactionsType.CAPTURE:
    case TransactionsType.AUTHORIZATION:
      return {
        icon: cardType === "VIRTUAL" ? SmartPhone04Icon : CreditCardPosIcon,
        name: cardType ? `${toTitleCase(cardType)} Card` : "Card",
      };
    case TransactionsType.REFUND:
    case TransactionsType.REFUND_PAYMENT:
      return { icon: ShoppingCart01Icon, name: "Refund" };
    // Direction is the useful half of an ACH row: both legs used to read
    // "ACH Transfer", so a table of them said nothing about where money went.
    case TransactionsType.INBOUND_TRANSFER:
      return { icon: BankIcon, name: "ACH In" };
    case TransactionsType.OUTBOUND_TRANSFER:
      return { icon: BankIcon, name: "ACH Out" };
    case TransactionsType.CHARGE_REPAYMENT:
      return { icon: BankIcon, name: "Repayment" };
    case TransactionsType.INBOUND_PAYMENT:
    case TransactionsType.INBOUND_FUNDS:
      return { icon: Payment02Icon, name: "Inbound Payment" };
    case TransactionsType.OUTBOUND_PAYMENT:
      return { icon: Payment02Icon, name: "Outbound Payment" };
    // Wires split by direction for the same reason the ACH legs do.
    case TransactionsType.INBOUND_WIRE:
      return { icon: BankIcon, name: "Wire In" };
    case TransactionsType.OUTBOUND_WIRE:
      return { icon: BankIcon, name: "Wire Out" };
    case TransactionsType.CASH:
      return { icon: Cash01Icon, name: "Cash" };
    case TransactionsType.MILEAGE:
      return { icon: Car03Icon, name: "Mileage" };
    case TransactionsType.INBOUND_REWARDS:
      return { icon: Medal06Icon, name: "Inbound Rewards" };
    case TransactionsType.REWARDS_TO_BALANCE:
      return { icon: Medal06Icon, name: "Rewards to balance" };
    case TransactionsType.REWARDS_TO_BANK:
      return { icon: BankIcon, name: "Rewards to bank" };
    case TransactionsType.FEE:
      return { icon: Coins01Icon, name: "Fee" };
    case TransactionsType.DISPUTE_CREDIT:
      return { icon: JusticeScale01Icon, name: "Dispute credit" };
    case TransactionsType.DISPUTE_CREDIT_REVERSAL:
      return { icon: JusticeScale01Icon, name: "Dispute credit reversal" };
    default:
      console.warn(`Unhandled transaction type: ${transactionType}`);
      return null;
  }
};

interface RenderTransactionTypeProps {
  transactionType: TransactionsType;
  cardType?: string;
  renderOption: RenderOption;
  iconStyle?: Omit<HugeiconsIconProps, "icon">;
}

export const RenderTransactionType: React.FC<RenderTransactionTypeProps> = ({
  transactionType,
  cardType,
  renderOption,
  iconStyle = {},
}) => {
  const { t } = useTranslation();
  const meta = transactionTypeMeta(transactionType, cardType);
  if (!meta) return null;

  if (renderOption === RenderOption.TEXT) {
    return t(meta.name);
  }

  return (
    <Tooltip
      disableInteractive
      title={t(meta.name)}
      slots={{
        transition: Zoom,
      }}
    >
      <HugeiconsIcon icon={meta.icon} {...iconStyle} />
    </Tooltip>
  );
};

interface RenderTransactionTypeChipProps {
  transactionType: TransactionsType;
  cardType?: string;
}

/**
 * The glyph and the label as one chip, for a table cell that shows both.
 *
 * The alternative is two `RenderTransactionType` renders side by side, which
 * gives the icon a tooltip repeating the label already next to it.
 */
export const RenderTransactionTypeChip: React.FC<
  RenderTransactionTypeChipProps
> = ({ transactionType, cardType }) => {
  const { t } = useTranslation();
  const meta = transactionTypeMeta(transactionType, cardType);
  if (!meta) return null;

  return (
    <Chip
      size="sm"
      tone="neutral"
      leading={<HugeiconsIcon icon={meta.icon} size={14} />}
    >
      {t(meta.name)}
    </Chip>
  );
};

export const formatTransactionType = (type: string): string => {
  return type
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
